import { useState, useEffect, useRef } from 'react';
import { getCookie, setCookie } from '../utils/cookies';

interface UseTimerReturn {
  timeSpentToday: number;
  timeLimitReached: boolean;
  dailyTimeLimit: number;
  setDailyTimeLimit: (minutes: number) => void;
  formatTimeRemaining: () => string;
}

// Cookie writes are throttled — driving them at 1 Hz used to flush state
// through React 60×/min for no UX gain.
const COOKIE_WRITE_INTERVAL_MS = 30 * 1000;

// Cap on how much wall-clock time a single tick is allowed to bank as
// "foreground time". The old wall-clock-anchored model used a stale anchor
// across visibility transitions: if visibilitychange's *show* event didn't
// fire (iOS Safari is notoriously flaky here), the next tick after the
// tab regained focus would add the entire hidden duration in one shot —
// users would see "5 mins remaining" suddenly become "0 mins" after coming
// back to a phone they'd left for an hour. By incrementing per-tick instead
// of from a baseline, and capping each tick at 2s, a missed visibilitychange
// can at worst cost us a single second of accuracy.
const MAX_TICK_GAIN_MS = 2000;

export const useTimer = (cookieConsent: boolean | null, hasFeeds: boolean = true): UseTimerReturn => {
  const [dailyTimeLimit, setDailyTimeLimitState] = useState<number>(30);
  const [timeSpentToday, setTimeSpentToday] = useState<number>(0);
  const [timeLimitReached, setTimeLimitReached] = useState<boolean>(false);

  // Source of truth for accumulated active time, kept in milliseconds so we
  // can bank sub-second tick gains without losing precision.
  const savedSpentMsRef = useRef<number>(0);
  // Wall-clock timestamp of the last tick. Advanced every tick (visible or
  // not) so a long hidden gap shows up as a single oversized delta — which
  // is then clamped to MAX_TICK_GAIN_MS.
  const lastTickRef = useRef<number>(Date.now());
  const lastCookieWriteRef = useRef<number>(0);
  // Track today's date so a session spanning midnight resets without
  // needing the user to reload.
  const dateRef = useRef<string>(new Date().toDateString());

  // Refs mirror the live values so the tick interval doesn't have to be
  // torn down and rebuilt every time an input changes.
  const dailyTimeLimitRef = useRef<number>(30);
  const cookieConsentRef = useRef<boolean | null>(cookieConsent);
  const hasFeedsRef = useRef<boolean>(hasFeeds);
  const timeLimitReachedRef = useRef<boolean>(false);

  useEffect(() => { dailyTimeLimitRef.current = dailyTimeLimit; }, [dailyTimeLimit]);
  useEffect(() => { cookieConsentRef.current = cookieConsent; }, [cookieConsent]);
  useEffect(() => { hasFeedsRef.current = hasFeeds; }, [hasFeeds]);
  useEffect(() => { timeLimitReachedRef.current = timeLimitReached; }, [timeLimitReached]);

  // Load saved values when consent flips to true.
  useEffect(() => {
    if (cookieConsent !== true) return;

    const savedTimeLimit = getCookie('daily_time_limit');
    const savedTimeSpent = getCookie('time_spent_today');
    const savedTimeSpentDate = getCookie('time_spent_date');
    const today = new Date().toDateString();
    dateRef.current = today;

    let limit = 30;
    if (savedTimeLimit) {
      limit = parseInt(savedTimeLimit, 10);
      setDailyTimeLimitState(limit);
    }

    if (savedTimeSpentDate !== today) {
      setCookie('time_spent_date', today, 1);
      setCookie('time_spent_today', '0', 1);
      savedSpentMsRef.current = 0;
      setTimeSpentToday(0);
      setTimeLimitReached(false);
    } else if (savedTimeSpent) {
      const spent = parseInt(savedTimeSpent, 10);
      savedSpentMsRef.current = spent * 1000;
      setTimeSpentToday(spent);
      if (limit > 0 && spent >= limit * 60) {
        setTimeLimitReached(true);
      }
    }

    // Anchor the tick clock to "now" so the first tick after a cold load
    // doesn't bank time that elapsed before the hook actually started.
    lastTickRef.current = Date.now();
  }, [cookieConsent]);

  // Save daily time limit when it changes.
  useEffect(() => {
    if (cookieConsent !== true) return;
    setCookie('daily_time_limit', dailyTimeLimit.toString());
  }, [dailyTimeLimit, cookieConsent]);

  // Single tick driver. Inputs are read from refs so this interval
  // is created once per mount.
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const sinceLast = now - lastTickRef.current;
      // Advance the anchor unconditionally so a long hidden gap can't
      // accumulate across many ticks.
      lastTickRef.current = now;

      // Cross-midnight reset during a continuous session.
      const today = new Date().toDateString();
      if (today !== dateRef.current) {
        dateRef.current = today;
        savedSpentMsRef.current = 0;
        setTimeSpentToday(0);
        setTimeLimitReached(false);
        if (cookieConsentRef.current === true) {
          setCookie('time_spent_date', today, 1);
          setCookie('time_spent_today', '0', 1);
          lastCookieWriteRef.current = now;
        }
        return;
      }

      if (timeLimitReachedRef.current) return;
      if (!hasFeedsRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;

      // Clamp the gain so a missed visibilitychange event can't bank
      // hours of hidden time as foreground time. Most ticks bank ~1s;
      // throttled tabs (Safari battery saver) can extend to 2s safely.
      const gainMs = Math.min(sinceLast, MAX_TICK_GAIN_MS);
      if (gainMs <= 0) return;

      savedSpentMsRef.current += gainMs;
      const newSpentSec = Math.floor(savedSpentMsRef.current / 1000);
      setTimeSpentToday(newSpentSec);

      const limit = dailyTimeLimitRef.current;
      if (limit > 0 && newSpentSec >= limit * 60) {
        setTimeLimitReached(true);
      }

      if (
        cookieConsentRef.current === true &&
        now - lastCookieWriteRef.current >= COOKIE_WRITE_INTERVAL_MS
      ) {
        setCookie('time_spent_today', newSpentSec.toString(), 1);
        lastCookieWriteRef.current = now;
      }
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  // Visibility transitions: flush on hide so we don't lose the last
  // unsynced seconds. The tick's MAX_TICK_GAIN_MS cap already covers the
  // case where this event fails to fire on the show side — we don't need
  // to mutate any anchor here.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (cookieConsentRef.current === true) {
          const sec = Math.floor(savedSpentMsRef.current / 1000);
          setCookie('time_spent_today', sec.toString(), 1);
          lastCookieWriteRef.current = Date.now();
        }
      } else {
        // Reset the tick anchor so the gap between hide and show isn't
        // recoloured as foreground time on the next tick.
        lastTickRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Flush cookie when leaving the page so we don't lose up to ~30s.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (cookieConsentRef.current !== true) return;
      const sec = Math.floor(savedSpentMsRef.current / 1000);
      setCookie('time_spent_today', sec.toString(), 1);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const setDailyTimeLimit = (minutes: number): void => {
    setDailyTimeLimitState(minutes);
    if (minutes === 0 || minutes * 60 > timeSpentToday) {
      setTimeLimitReached(false);
    }
  };

  const formatTimeRemaining = (): string => {
    if (dailyTimeLimit === 0) {
      return 'Unlimited';
    }
    const totalSeconds = Math.max(0, dailyTimeLimit * 60 - timeSpentToday);
    const minutes = Math.ceil(totalSeconds / 60);
    return `${minutes}`;
  };

  return {
    timeSpentToday,
    timeLimitReached,
    dailyTimeLimit,
    setDailyTimeLimit,
    formatTimeRemaining,
  };
};
